import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '../components/ui/collapsible';
import { toast } from 'sonner';
import { 
  ArrowLeft, Users, Package, DollarSign, TrendingUp, ChevronDown, ChevronRight,
  Phone, Mail, MapPin, Building2, FileText, Calendar
} from 'lucide-react';
import { useSettings } from '../context/SettingsContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CustomerHistory = () => {
  const { customerId } = useParams();
  const navigate = useNavigate();
  const [history, setHistory] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expandedRows, setExpandedRows] = useState({});
  const { formatCurrency } = useSettings();

  useEffect(() => {
    fetchHistory();
  }, [customerId]);

  const fetchHistory = async () => {
    try {
      const response = await axios.get(`${API}/customers/${customerId}/history`);
      setHistory(response.data);
    } catch (error) {
      toast.error('Failed to load customer history');
      navigate('/customers');
    } finally {
      setLoading(false);
    }
  };

  const toggleRowExpand = (issuanceId) => {
    setExpandedRows(prev => ({
      ...prev,
      [issuanceId]: !prev[issuanceId]
    }));
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleString();
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-slate-900"></div>
      </div>
    );
  }

  if (!history) {
    return null;
  }

  const { customer, total_issuances, total_parts_used, total_cost, total_value, total_profit, issuances } = history;

  return (
    <div className="space-y-6 animate-fade-in" data-testid="customer-history-page">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button
          variant="outline"
          size="sm"
          onClick={() => navigate('/customers')}
          className="rounded-sm"
        >
          <ArrowLeft className="w-4 h-4 mr-1" />
          Back
        </Button>
        <div>
          <h1 className="text-3xl font-black text-slate-900 tracking-tight">{customer.name}</h1>
          {customer.company_name && (
            <p className="text-slate-500 flex items-center gap-1">
              <Building2 className="w-4 h-4" />
              {customer.company_name}
            </p>
          )}
        </div>
      </div>

      {/* Customer Info & Summary */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {/* Customer Details */}
        <Card className="border-slate-200 shadow-sm rounded-sm">
          <CardHeader className="border-b bg-slate-50 py-3">
            <CardTitle className="text-sm font-semibold flex items-center gap-2">
              <Users className="w-4 h-4" />
              Customer Details
            </CardTitle>
          </CardHeader>
          <CardContent className="p-4 space-y-2">
            {customer.phone && (
              <p className="text-sm flex items-center gap-2 text-slate-600">
                <Phone className="w-4 h-4 text-slate-400" />
                {customer.phone}
              </p>
            )}
            {customer.email && (
              <p className="text-sm flex items-center gap-2 text-slate-600">
                <Mail className="w-4 h-4 text-slate-400" />
                {customer.email}
              </p>
            )}
            {(customer.address || customer.city) && (
              <p className="text-sm flex items-center gap-2 text-slate-600">
                <MapPin className="w-4 h-4 text-slate-400" />
                {[customer.address, customer.city].filter(Boolean).join(', ')}
              </p>
            )}
            {customer.notes && (
              <p className="text-sm text-slate-500 pt-2 border-t mt-2">{customer.notes}</p>
            )}
          </CardContent>
        </Card>

        {/* Summary Stats */}
        <Card className="border-slate-200 shadow-sm rounded-sm md:col-span-2">
          <CardHeader className="border-b bg-slate-50 py-3">
            <CardTitle className="text-sm font-semibold flex items-center gap-2">
              <TrendingUp className="w-4 h-4" />
              Summary
            </CardTitle>
          </CardHeader>
          <CardContent className="p-4">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="text-center p-3 bg-slate-50 rounded-sm">
                <p className="text-2xl font-bold text-slate-900">{total_issuances}</p>
                <p className="text-xs text-slate-500 uppercase">Issuances</p>
              </div>
              <div className="text-center p-3 bg-slate-50 rounded-sm">
                <p className="text-2xl font-bold text-slate-900">{total_parts_used}</p>
                <p className="text-xs text-slate-500 uppercase">Parts Used</p>
              </div>
              <div className="text-center p-3 bg-red-50 rounded-sm">
                <p className="text-2xl font-bold text-red-600">{formatCurrency(total_cost)}</p>
                <p className="text-xs text-slate-500 uppercase">Company Cost</p>
              </div>
              <div className="text-center p-3 bg-emerald-50 rounded-sm">
                <p className="text-2xl font-bold text-emerald-600">{formatCurrency(total_value)}</p>
                <p className="text-xs text-slate-500 uppercase">Total Value</p>
              </div>
            </div>
            <div className="mt-4 p-3 bg-blue-50 rounded-sm flex justify-between items-center">
              <span className="text-sm font-semibold text-blue-700">Total Profit</span>
              <span className="text-xl font-bold text-blue-700">{formatCurrency(total_profit)}</span>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Issuance History */}
      <Card className="border-slate-200 shadow-sm rounded-sm">
        <CardHeader className="border-b bg-slate-50">
          <CardTitle className="text-lg font-bold flex items-center gap-2">
            <FileText className="w-5 h-5" />
            Stock Issuance History
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {issuances.length > 0 ? (
            <Table className="data-table">
              <TableHeader>
                <TableRow>
                  <TableHead className="w-8"></TableHead>
                  <TableHead>Date</TableHead>
                  <TableHead>Issuance #</TableHead>
                  <TableHead>Quotation #</TableHead>
                  <TableHead>Location</TableHead>
                  <TableHead className="text-right">Cost</TableHead>
                  <TableHead className="text-right">Value</TableHead>
                  <TableHead className="text-right">Profit</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Issued By</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {issuances.map(issuance => (
                  <Collapsible key={issuance.id} open={expandedRows[issuance.id]} asChild>
                    <>
                      <TableRow 
                        className={issuance.status === 'cancelled' ? 'opacity-50' : ''}
                        data-testid={`issuance-row-${issuance.id}`}
                      >
                        <TableCell>
                          <CollapsibleTrigger asChild>
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-6 w-6 p-0"
                              onClick={() => toggleRowExpand(issuance.id)}
                            >
                              {expandedRows[issuance.id] ? (
                                <ChevronDown className="h-4 w-4" />
                              ) : (
                                <ChevronRight className="h-4 w-4" />
                              )}
                            </Button>
                          </CollapsibleTrigger>
                        </TableCell>
                        <TableCell className="text-sm text-slate-600">
                          <div className="flex items-center gap-1">
                            <Calendar className="w-3 h-3" />
                            {formatDate(issuance.issued_at)}
                          </div>
                        </TableCell>
                        <TableCell className="font-mono font-semibold text-slate-900">
                          {issuance.issuance_number}
                        </TableCell>
                        <TableCell className="font-mono text-blue-600">
                          {issuance.quotation_number}
                        </TableCell>
                        <TableCell>
                          <Badge variant="secondary" className="rounded-sm">
                            {issuance.location_name}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-right font-mono text-sm text-red-600">
                          {formatCurrency(issuance.total_cost)}
                        </TableCell>
                        <TableCell className="text-right font-mono text-sm">
                          {formatCurrency(issuance.total_value)}
                        </TableCell>
                        <TableCell className="text-right font-mono text-sm font-semibold text-emerald-600">
                          {formatCurrency(issuance.profit)}
                        </TableCell>
                        <TableCell>
                          <Badge className={`rounded-sm ${
                            issuance.status === 'completed' 
                              ? 'bg-emerald-100 text-emerald-700' 
                              : 'bg-red-100 text-red-700'
                          }`}>
                            {issuance.status}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-sm text-slate-600">
                          {issuance.issued_by}
                        </TableCell>
                      </TableRow>
                      <CollapsibleContent asChild>
                        <TableRow className="bg-slate-50 hover:bg-slate-50">
                          <TableCell colSpan={10} className="p-4">
                            <div className="space-y-3">
                              <p className="text-sm font-semibold text-slate-700">Parts Issued</p>
                              <div className="border rounded-sm bg-white divide-y">
                                {issuance.items?.map((item, idx) => (
                                  <div key={idx} className="p-3 flex justify-between items-center">
                                    <div className="flex items-center gap-3">
                                      <Package className="w-5 h-5 text-slate-400" />
                                      <div>
                                        <p className="font-mono font-semibold">{item.part_reference}</p>
                                        <p className="text-xs text-slate-500">{item.part_description}</p>
                                      </div>
                                    </div>
                                    <div className="text-right">
                                      <p className="text-sm">
                                        <span className="font-semibold">{item.quantity}</span> × {formatCurrency(item.selling_price)}
                                      </p>
                                      <p className="text-xs text-slate-500">
                                        Cost: {formatCurrency(item.buying_price)} each
                                      </p>
                                      <p className="font-semibold">{formatCurrency(item.quantity * item.selling_price)}</p>
                                    </div>
                                  </div>
                                ))}
                              </div>
                              {issuance.notes && (
                                <div className="p-2 bg-amber-50 rounded-sm border border-amber-200">
                                  <p className="text-xs text-amber-700"><strong>Notes:</strong> {issuance.notes}</p>
                                </div>
                              )}
                            </div>
                          </TableCell>
                        </TableRow>
                      </CollapsibleContent>
                    </>
                  </Collapsible>
                ))}
              </TableBody>
            </Table>
          ) : (
            <div className="flex flex-col items-center justify-center py-16 text-slate-400">
              <Package className="w-12 h-12 mb-4" />
              <p className="text-lg font-medium">No issuances yet</p>
              <p className="text-sm">Issue stock to this customer to see history here</p>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default CustomerHistory;
